import { useMemo, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";
import Dropdown from "../../inputs/dropdown";
import Input from "../../inputs/input";
import { getCardPaymentsToRefundFromOrder } from "../../../utils/payment-utils";


const CardCouponSelector = ({ order, form, handleInputChange, hasTerminal = false, refundAmount = 0, onValidationChange }) => {
  const [cardList, setCardList] = useState([]);

  useEffect(() => {
      const cardpayments = getCardPaymentsToRefundFromOrder(order);

      const mapData = cardpayments?.map(obj => ({
        id: obj.paymentId,
        label: "Tarjeta (" + obj.lastCardDigits + ") - $" + obj.totalAmount,
        amount: obj.totalAmount
      }));

      const filteredData = getFilteredCardList(mapData, refundAmount);
      setCardList(filteredData)
  }, [order.payments, refundAmount])

  const getSelectedAmount = (paymentId) => {
    const selectedCard = cardList.find(card => card.id === paymentId);
    return selectedCard ? selectedCard.amount : 0;
  };

  const getCurrentTotal = () =>
    getSelectedAmount(form.payment1) + getSelectedAmount(form.payment2);

  const handlePayment1Change = (event) => {
    const updatedForm = {
      ...form,
      [event.target.name]: event.target.value,
      payment2: '',
      cuponNumber2: ''
    };

    const syntheticEvent = {
      target: {
        name: 'multipleFields',
        value: updatedForm
      }
    };

    handleInputChange(syntheticEvent);
  };

  const isValidCupon = () => {
    if(!form.payment1)
        return false;

    if(!hasTerminal && !Number(form.cuponNumber1) > 0)
      return false;

    if(!hasTerminal && cardList.length > 1 && form.payment2 && !Number(form.cuponNumber2) > 0)
      return false;

    if(refundAmount > 0 && getCurrentTotal() < refundAmount)
      return false;

    return true;
  };

  useMemo(() => {
    if (onValidationChange) {
      onValidationChange(isValidCupon());
    }
  }, [form.payment1, form.payment2, form.cuponNumber1, form.cuponNumber2, hasTerminal, cardList, refundAmount, onValidationChange]);

  const needsSecondPayment = useMemo(() => {
    if (!form.payment1) return false;
    const firstPaymentAmount = getSelectedAmount(form.payment1);
    return firstPaymentAmount < refundAmount;
  }, [form.payment1, cardList, refundAmount]);

  const getAvailableCards = (excludePaymentId) =>
    cardList.filter(card => card.id !== excludePaymentId);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='inherit' color='primary'>
          {hasTerminal ? 'Seleccionar tarjeta' : 'Número de Cupón:'}
          {refundAmount > 0 && ` (Monto a devolver: $${refundAmount})`}
        </Typography>
      </Grid>

      {/* First payment */}
      <Grid size={12}>
        <Dropdown
          fullWidth
          onChange={handlePayment1Change}
          size='small'
          label="Tarjeta"
          name="payment1"
          getOptionLabel={(option) => option?.label}
          getOptionValue={(option) => option?.id}
          value={form.payment1 || ''}
          values={cardList}
        />
      </Grid>
      { !hasTerminal && form.payment1 &&
        <Grid size={12}>
          <Input
            required
            fullWidth
            label="Numero de Cupón"
            name="cuponNumber1"
            type="number"
            onChange={handleInputChange}
            value={form.cuponNumber1}
          />
        </Grid>
      }

      {/* Second payment - only show if first payment doesn't cover refund amount */}
      { needsSecondPayment &&
        <>
          <Grid size={12}>
            <Dropdown
              fullWidth
              onChange={handleInputChange}
              size='small'
              label="Tarjeta 2"
              name="payment2"
              getOptionLabel={(option) => option?.label}
              getOptionValue={(option) => option?.id}
              value={form.payment2 || ''}
              values={getAvailableCards(form.payment1)}
            />
          </Grid>
          { !hasTerminal && form.payment2 &&
            <Grid size={12}>
              <Input
                required
                fullWidth
                label="Numero de Cupón 2"
                name="cuponNumber2"
                type="number"
                onChange={handleInputChange}
                value={form.cuponNumber2}
              />
            </Grid>
          }
        </>
      }

      {/* Show current total vs refund amount */}
      { form.payment1 && refundAmount > 0 &&
        <Grid size={12}>
          <Typography variant='body2' color={getCurrentTotal() >= refundAmount ? 'success.main' : 'warning.main'}>
            Total seleccionado: ${getCurrentTotal()} / ${refundAmount}
            {getCurrentTotal() >= refundAmount ? ' ✓' : ' (Falta: $' + (refundAmount - getCurrentTotal()) + ')'}
          </Typography>
        </Grid>
      }
    </Grid>
  );
};


const getFilteredCardList = (cardList, refundAmount) => {
  if (refundAmount <= 0 || cardList.length <= 1) {
    return cardList;
  }

  const cardsThatCoverRefund = cardList.filter(card => card.amount >= refundAmount);

  if (cardsThatCoverRefund.length === 1) {
    return cardsThatCoverRefund;
  }

  return cardList;
};

export default CardCouponSelector;